import { defineStore } from 'pinia'
import { journalsApi } from '../api/journalsApi'
import { useErrorHandler } from '../composables/useErrorHandler'
import { DEFAULT_PAGE_SIZE } from '../constants/pagination'
import type { JournalAttribute, JournalEntry, PaginatedResponse } from '../types/journal.types'
import type { CreateEntryPayload, UpdateEntryPayload } from '../types/entry.types'
import { useJournalDetailStore } from './journalDetailStore'

type JournalEntriesResponse = PaginatedResponse<JournalEntry>

function buildFilterParams(
  filters: Record<string, unknown>,
  attrs: JournalAttribute[],
): Record<string, string | number | string[]> {
  const p: Record<string, string | number | string[]> = {}
  for (const [attrKey, value] of Object.entries(filters)) {
    if (value === null || value === undefined) continue
    if (Array.isArray(value) && value.length === 0) continue
    if (typeof value === 'string' && value === '') continue
    if (attrKey === 'creator_name' && typeof value === 'string') {
      p.creator__icontains = value
      continue
    }
    if (attrKey === 'created_at' && value && typeof value === 'object' && ('from' in value || 'to' in value)) {
      const v = value as { from?: string; to?: string }
      if (v.from) p.created_after = v.from
      if (v.to) p.created_before = v.to
      continue
    }
    const attr = attrs.find(a => String(a.id) === attrKey)
    if (!attr) continue
    const templateAttributeId = attr.id
    const t = attr?.attribute_type
    const isMulti = ['option', 'choice', 'multi_option', 'multiple_choice', 'content_object'].includes(t || '')
    if (['text', 'string'].includes(t || '')) {
      p[`template_attribute__${templateAttributeId}__icontains`] = String(value)
    } else if (['integer', 'float'].includes(t || '')) {
      if (typeof value === 'object' && value !== null && ('from' in value || 'to' in value)) {
        const v = value as { from?: number; to?: number }
        if (v.from != null) p[`template_attribute__${templateAttributeId}__gte`] = v.from
        if (v.to != null) p[`template_attribute__${templateAttributeId}__lte`] = v.to
      } else {
        p[`template_attribute__${templateAttributeId}`] = Number(value)
      }
    } else if (t === 'boolean') {
      p[`template_attribute__${templateAttributeId}`] = value === true ? 'true' : 'false'
    } else if (['date', 'date_time'].includes(t || '')) {
      const v = value as { from?: string; to?: string }
      if (v?.from) p[`template_attribute__${templateAttributeId}__gte`] = v.from
      if (v?.to) p[`template_attribute__${templateAttributeId}__lte`] = v.to
    } else if (t === 'time') {
      p[`template_attribute__${templateAttributeId}`] = String(value)
    } else if (t === 'file') {
      p[`template_attribute__${templateAttributeId}__isnull`] = value === true ? 'false' : 'true'
    } else if (isMulti) {
      p[`template_attribute__${templateAttributeId}`] = (value as string[]).map(String).join(',')
    }
  }
  return p
}

export const useJournalEntriesStore = defineStore('journalEntries', {
  state: () => ({
    journalEntries: [] as JournalEntry[],
    isFetchingEntries: false,
    entriesError: null as unknown | null,
    entriesResponseData: null as JournalEntriesResponse | null,
    entrySearchQuery: '',
    itemsPerPage: DEFAULT_PAGE_SIZE,
    cursor: undefined as string | undefined,
    entrySort: '-created_at' as string,
    entryFilters: {} as Record<string, unknown>,
  }),

  getters: {
    totalEntries(state): number | undefined {
      return state.journalEntries.length
    },

    totalEntriesCount(state): number | undefined {
      return state.entriesResponseData?.count
    },
  },

  actions: {
    async fetchJournalEntries(journalId: number): Promise<void> {
      this.isFetchingEntries = true
      this.entriesError = null
      const { handleError } = useErrorHandler()
      const detailStore = useJournalDetailStore()

      try {
        const params: Record<string, string | number | string[] | undefined> = {
          cursor: this.cursor,
          page_size: this.itemsPerPage,
        }
        if (this.entrySort) params.sort = this.entrySort
        if (this.entrySearchQuery?.trim()) params.search = this.entrySearchQuery.trim()
        Object.assign(params, buildFilterParams(this.entryFilters, detailStore.journalAttributes))

        const data = await journalsApi.getJournalEntries(journalId, params)
        this.journalEntries = data.results
        this.entriesResponseData = {
          ...data,
          count: data.count ?? this.entriesResponseData?.count,
        }
      } catch (e) {
        console.error('[store.fetchJournalEntries] Ошибка:', e)
        this.entriesError = e
        handleError(e, 'Ошибка загрузки записей журнала')
      } finally {
        this.isFetchingEntries = false
      }
    },

    setEntryFilter(attrName: string, value: unknown) {
      if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
        const { [attrName]: _, ...rest } = this.entryFilters
        this.entryFilters = rest
      } else {
        this.entryFilters = { ...this.entryFilters, [attrName]: value }
      }
    },

    clearEntryFilters() {
      this.entryFilters = {}
    },

    async applyEntryFiltersAndFetch(journalId: number): Promise<void> {
      this.cursor = undefined
      await this.fetchJournalEntries(journalId)
    },

    setEntrySort(sort: string) {
      this.entrySort = sort
    },

    setCursor(cursor: string | undefined) {
      this.cursor = cursor
    },

    setEntrySearchQuery(query: string) {
      this.entrySearchQuery = query
    },

    async createJournalEntry(
      journalId: number,
      entryData: CreateEntryPayload,
    ): Promise<JournalEntry> {
      const { handleError } = useErrorHandler()
      try {
        const entry = await journalsApi.createEntry(journalId, entryData)
        this.journalEntries.unshift(entry)
        return entry
      } catch (e) {
        handleError(e, 'Ошибка создания записи журнала')
        throw e
      }
    },

    async updateJournalEntry(
      journalId: number,
      entryId: number,
      entryData: UpdateEntryPayload,
    ): Promise<JournalEntry> {
      const { handleError } = useErrorHandler()
      try {
        const entry = await journalsApi.updateEntry(journalId, entryId, entryData)
        const index = this.journalEntries.findIndex(e => e.id === entryId)
        if (index !== -1) {
          this.journalEntries[index] = entry
        }
        return entry
      } catch (e) {
        handleError(e, 'Ошибка обновления записи журнала')
        throw e
      }
    },

    async deleteJournalEntry(journalId: number, entryId: number): Promise<void> {
      const { handleError } = useErrorHandler()
      try {
        await journalsApi.deleteEntry(journalId, entryId)
        const index = this.journalEntries.findIndex(e => e.id === entryId)
        if (index !== -1) {
          this.journalEntries.splice(index, 1)
        }
      } catch (e) {
        handleError(e, 'Ошибка удаления записи журнала')
        throw e
      }
    },

    clearEntriesData() {
      this.journalEntries = []
      this.entriesResponseData = null
      this.entrySearchQuery = ''
      this.entrySort = '-created_at'
      this.entryFilters = {}
    },
  },
})
